import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Line, useGLTF } from '@react-three/drei';
import { createCurvedLamellaGeometry } from '../../../containers/Fiber/3dObjects/curvedLamellaGeometry';
import { DEFAULT_HYPERBEAM_MM_TO_SCENE, buildHyperBeamPreviewModel } from './hyperBeamUtils';

function createLamellaMaterial(baseMaterial, color) {
  const tint = new THREE.Color(color || '#c48a57');

  if (!baseMaterial) {
    return new THREE.MeshStandardMaterial({
      color: tint,
      roughness: 0.72,
      metalness: 0.05
    });
  }

  const material = baseMaterial.clone();
  if (material.color) {
    material.color.copy(tint);
  }
  if (material.emissive) {
    material.emissive.copy(tint);
    material.emissiveIntensity = 0.045;
  }
  material.roughness = baseMaterial.roughness ?? 0.72;
  material.metalness = baseMaterial.metalness ?? 0.05;
  material.needsUpdate = true;
  return material;
}

function HyperBeamLamella({ lamella, baseMaterial, lamellaIndex }) {
  const geometry = useMemo(
    () =>
      createCurvedLamellaGeometry({
        points3d: lamella.pointsMm,
        crossSectionWidth: lamella.widthMm,
        crossSectionHeight: lamella.thicknessMm,
        geometryRotationX: 0,
        exactPath: true
      }),
    [lamella]
  );

  const edgeGeometry = useMemo(() => {
    if (!geometry) {
      return null;
    }
    return new THREE.EdgesGeometry(geometry, 24);
  }, [geometry]);

  const material = useMemo(() => {
    const resolved = createLamellaMaterial(baseMaterial, lamella.color);
    const modulation = lamellaIndex % 2 === 0 ? 0.035 : -0.02;

    if (resolved?.color) {
      resolved.color.offsetHSL(0, 0, modulation);
    }

    return resolved;
  }, [baseMaterial, lamella.color, lamellaIndex]);

  const edgeMaterial = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: '#2e2419',
        transparent: true,
        opacity: 0.28,
        depthWrite: false
      }),
    []
  );

  useEffect(() => {
    return () => {
      geometry?.dispose?.();
      edgeGeometry?.dispose?.();
      material?.dispose?.();
      edgeMaterial?.dispose?.();
    };
  }, [edgeGeometry, edgeMaterial, geometry, material]);

  if (!geometry) {
    return null;
  }

  return (
    <group>
      <mesh geometry={geometry} material={material} />
      {edgeGeometry ? <lineSegments geometry={edgeGeometry} material={edgeMaterial} renderOrder={3} /> : null}
    </group>
  );
}

export default function HyperBeamModel({ hyperBeam, beamOptions }) {
  const { nodes, materials } = useGLTF('/les.glb');
  const baseMaterial = nodes?.lamela?.material || materials?.lamela || nodes?.paket?.material || materials?.paket || null;

  const previewModel = useMemo(() => buildHyperBeamPreviewModel(hyperBeam, beamOptions), [hyperBeam, beamOptions]);

  if (!previewModel || previewModel.status !== 'ready') {
    return null;
  }

  const sceneOffset = previewModel.sceneOffsetMm || { x: 0, y: 0, z: 0 };
  const sceneScale = hyperBeam?.mmToScene || DEFAULT_HYPERBEAM_MM_TO_SCENE;
  const helperY = previewModel.helperYOffsetMm || 0;
  const analysisMarker = hyperBeam?.analysisMarker || null;
  const analysisMarkerAngle = Number.isFinite(analysisMarker?.angleDeg) ? (analysisMarker.angleDeg * Math.PI) / 180 : 0;
  const analysisPlaneHeight = (previewModel.lamellaWidthMm || 0) + 120;
  const analysisPlaneWidth = Math.max((previewModel.lamellaThicknessMm || 0) * 3, 70);
  const analysisHighlightIndex = analysisMarker?.nearestShoe?.index ?? null;

  return (
    <group position={[sceneOffset.x * sceneScale, (sceneOffset.y || 0) * sceneScale, sceneOffset.z * sceneScale]}>
      <group scale={[sceneScale, sceneScale, sceneScale]}>
        <Line points={previewModel.guideCurve.map((point) => [point.x, helperY, point.z])} color="#6f695f" lineWidth={0.9} />
        <Line points={previewModel.coreGuideCurve.map((point) => [point.x, helperY, point.z])} color="#b03ad6" lineWidth={1.6} />

        {previewModel.shoeMarkers.map((shoe) => (
          <group key={`shoe-${shoe.index}`}>
            <Line
              points={[
                [shoe.baseX ?? shoe.x, -previewModel.lamellaWidthMm / 2 - 56, shoe.baseZ ?? shoe.z],
                [shoe.baseX ?? shoe.x, previewModel.lamellaWidthMm / 2 + 56, shoe.baseZ ?? shoe.z]
              ]}
              color={shoe.index === analysisHighlightIndex ? '#ff8a26' : '#4e4e4e'}
              lineWidth={1.15}
            />
            <Line
              points={[
                [shoe.baseX ?? shoe.x, helperY, shoe.baseZ ?? shoe.z],
                [shoe.x, helperY, shoe.z],
                [shoe.endX, helperY, shoe.endZ]
              ]}
              color={shoe.index === analysisHighlightIndex ? '#ff8a26' : '#4d4d4d'}
              lineWidth={1.15}
            />
            <mesh position={[shoe.x, helperY, shoe.z]}>
              <sphereGeometry args={[11, 10, 10]} />
              <meshStandardMaterial color={shoe.index === analysisHighlightIndex ? '#ff8a26' : '#5b5b5b'} roughness={0.75} metalness={0.18} depthWrite={false} />
            </mesh>
            <mesh position={[shoe.baseX ?? shoe.x, helperY, shoe.baseZ ?? shoe.z]}>
              <sphereGeometry args={[6, 8, 8]} />
              <meshStandardMaterial color="#9a9a9a" roughness={0.82} metalness={0.08} depthWrite={false} />
            </mesh>
          </group>
        ))}

        {previewModel.referenceMarker ? (
          <mesh position={[previewModel.referenceMarker.x, helperY, previewModel.referenceMarker.z]} renderOrder={4}>
            <sphereGeometry args={[16, 12, 12]} />
            <meshStandardMaterial color="#d83f5b" roughness={0.55} metalness={0.15} depthWrite={false} />
          </mesh>
        ) : null}

        {analysisMarker?.point ? (
          <group position={[analysisMarker.point.x, 0, analysisMarker.point.z]} rotation={[0, analysisMarkerAngle, 0]}>
            <mesh renderOrder={5}>
              <planeGeometry args={[analysisPlaneWidth, analysisPlaneHeight]} />
              <meshStandardMaterial color="#17a2ff" transparent opacity={0.32} side={THREE.DoubleSide} depthWrite={false} />
            </mesh>
            <mesh position={[0, helperY, 0]} renderOrder={6}>
              <sphereGeometry args={[14, 12, 12]} />
              <meshStandardMaterial color="#17a2ff" roughness={0.42} metalness={0.08} depthWrite={false} />
            </mesh>
          </group>
        ) : null}

        {previewModel.lamellas.map((lamella, lamellaIndex) => (
          <HyperBeamLamella key={lamella.key} lamella={lamella} baseMaterial={baseMaterial} lamellaIndex={lamellaIndex} />
        ))}
      </group>
    </group>
  );
}

useGLTF.preload('/les.glb');
